var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useApolloClient, useMutation } from '@apollo/client/react';
import { DocumentMetadataPropertySetterEnum } from 'shared/domains/apollo/generated/types';
import useAccount from 'shared/domains/users/hooks/useAccount';
import { CreateDocumentMetadataPairDocument, DeleteDocumentMetadataPairDocument, DocumentMetadataPairFragmentFragmentDoc, UpdateDocumentMetadataPairDocument, VerifyDocumentMetadataPairDocument, } from './mutations.gql';
export function useMetadataActions() {
    const client = useApolloClient();
    const { data: accountData } = useAccount();
    const { userId } = accountData;
    const [createMutation, createState] = useMutation(CreateDocumentMetadataPairDocument);
    const [updateMutation, updateState] = useMutation(UpdateDocumentMetadataPairDocument);
    const [deleteMutation, deleteState] = useMutation(DeleteDocumentMetadataPairDocument);
    const [verifyMutation, verifyState] = useMutation(VerifyDocumentMetadataPairDocument);
    const mutationLoading = createState.loading || updateState.loading || deleteState.loading || verifyState.loading;
    const mutationError = createState.error || createState.error || deleteState.error || verifyState.error;
    const handleVerifyTags = (variables) => __awaiter(this, void 0, void 0, function* () {
        const { pairIds, onCompleted } = variables;
        return verifyMutation({
            variables,
            onCompleted,
            onQueryUpdated(observableQuery) {
                return observableQuery.refetch();
            },
            update(cache) {
                if (pairIds) {
                    pairIds.forEach((id) => {
                        const cacheId = cache.identify({ id, __typename: 'DocumentMetadataPair' });
                        cache.modify({
                            id: cacheId,
                            fields: {
                                approvedByUserId() {
                                    return userId;
                                },
                                lastSetBy: () => DocumentMetadataPropertySetterEnum.User,
                            },
                        });
                    });
                }
            },
        });
    });
    const handleCreate = (createData) => {
        const { propertyName, value, documentId } = createData;
        return createMutation({
            variables: {
                propertyName,
                value,
                documentId,
            },
            update(cache, { data }) {
                cache.modify({
                    fields: {
                        documentMetadataPairs(existing = []) {
                            const { createDocumentMetadataPair } = data || {};
                            if (createDocumentMetadataPair) {
                                const cacheId = cache.identify({
                                    id: createDocumentMetadataPair.id,
                                    // eslint-disable-next-line no-underscore-dangle
                                    __typename: createDocumentMetadataPair.__typename,
                                });
                                const newPair = cache.writeFragment({
                                    id: cacheId,
                                    fragment: DocumentMetadataPairFragmentFragmentDoc,
                                    data: createDocumentMetadataPair,
                                });
                                return [...existing, newPair];
                            }
                            return existing;
                        },
                    },
                });
            },
        });
    };
    const handleUpdate = (updateData) => {
        const { id, value } = updateData;
        const existingData = client.cache.readFragment({
            id: client.cache.identify({ id, __typename: 'DocumentMetadataPair' }),
            fragment: DocumentMetadataPairFragmentFragmentDoc,
        });
        return updateMutation({
            variables: {
                id,
                input: {
                    value,
                },
            },
            update(cache, { data }) {
                cache.modify({
                    fields: {
                        documentMetadataPairs(existing = []) {
                            const { updateDocumentMetadataPair } = data || {};
                            if (updateDocumentMetadataPair) {
                                const cacheId = cache.identify({
                                    id: updateDocumentMetadataPair.id,
                                    // eslint-disable-next-line no-underscore-dangle
                                    __typename: updateDocumentMetadataPair.__typename,
                                });
                                cache.writeFragment({
                                    id: cacheId,
                                    fragment: DocumentMetadataPairFragmentFragmentDoc,
                                    data: updateDocumentMetadataPair,
                                });
                            }
                            return existing;
                        },
                    },
                });
            },
            optimisticResponse(vars, { IGNORE }) {
                if (!existingData) {
                    return IGNORE;
                }
                return {
                    updateDocumentMetadataPair: Object.assign(Object.assign({}, existingData), { value: vars.input.value }),
                };
            },
        });
    };
    const handleDelete = (id) => deleteMutation({
        variables: {
            id,
        },
        update(cache, { data }) {
            const { deleteDocumentMetadataPair } = data || {};
            const { __typename } = deleteDocumentMetadataPair || {};
            const cacheId = cache.identify({ id, __typename });
            cache.modify({
                id: cacheId,
                fields(_, { DELETE }) {
                    return DELETE;
                },
            });
        },
    });
    return {
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        verify: handleVerifyTags,
        error: mutationError,
        loading: mutationLoading,
    };
}
